import { useQuery } from "@tanstack/react-query";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineDocumentFilled } from "assets/svg/outline-document-filled.svg";
import { NOTES_TYPES } from "components/Dashboard/DoctorDashboard/notes-and-forms/types";
import Loading from "components/Loading";
import CopyToClipboardButton from "components/copy-to-clipboard-button/CopyToClipboardButton";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import { debounce } from "lodash";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import createNoteOrFormData from "services/createNoteOrFormData";
import putNoteComment from "services/putNoteComment";
import putNoteOrFormData from "services/putNoteOrFormData";
import queryMetaData from "services/queryMetaData";
import querySecureData from "services/querySecureData";
import {
  notesCustomApi,
  useGetAllNotesQuery,
} from "store/services/notesCustomApi";
import {
  orchid_decode_key,
  orchid_decrypt,
  orchid_encrypt,
} from "utils/OrchidEncryption";
import styles from "./PsychotherapyNotes.module.scss";

const AUTOSAVE_DEBOUNCE = 3000;

const PsychotherapyNotes = ({
  patientId,
  appointmentId,
}: {
  patientId: string;
  appointmentId: string;
}) => {
  const dispatch = useDispatch();
  const commentInputRef = useRef<HTMLTextAreaElement>(null);

  const [noteDataId, setNoteDataId] = useState<number>();
  const [comment, setComment] = useState("");
  const [decodeKey, setDecodeKey] = useState<CryptoKey>();
  const [isLoadingPreexistingNoteComment, setIsLoadingPreexistingNoteComment] =
    useState(false);
  const [isSavingComment, setIsSavingComment] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const { data: preexistingNotes, isLoading: isLoadingPreexistingNotes } =
    useGetAllNotesQuery({
      appointmentId,
    });

  const isLoading = useMemo(
    () => isLoadingPreexistingNotes || isLoadingPreexistingNoteComment,
    [isLoadingPreexistingNotes, isLoadingPreexistingNoteComment],
  );

  const initializeWithPreexistingNote = useCallback(async () => {
    if (!noteDataId) {
      return;
    }

    setIsLoadingPreexistingNoteComment(true);
    try {
      const secureData = await querySecureData({
        type: "note",
        id: noteDataId,
      });

      const metaData = await queryMetaData({ type: "note", id: noteDataId });
      const decodeKey = await orchid_decode_key(metaData.cipher_key);
      setDecodeKey(decodeKey);

      const comment = await orchid_decrypt(
        secureData.note_comment ?? "",
        decodeKey,
      );

      setComment(comment);
    } catch (error) {
      console.error(error);
    } finally {
    }
    setIsLoadingPreexistingNoteComment(false);
  }, [noteDataId]);

  const attachNewNote = useCallback(
    async (noteSchema: NoteSchema) => {
      if (!patientId || !appointmentId || !noteSchema) {
        return;
      }

      const noteMetaData = await createNoteOrFormData(NOTES_TYPES.note, {
        appointment_id: appointmentId,
        patient_cognito_sub: patientId,
      });

      const decodeKey = await orchid_decode_key(noteMetaData.cipher_key);
      setDecodeKey(decodeKey);

      const noteData = await putNoteOrFormData(
        NOTES_TYPES.note,
        noteMetaData.note_data_id,
        {
          note_schema_id: noteSchema.note_schema_id,
          note_schema_revision: noteSchema.note_schema_revision,
          patient_cognito_sub: patientId,
        },
      );
      setNoteDataId(noteData.note_data_id);

      setComment(noteData.note_comment ?? "");

      dispatch(
        notesCustomApi.util.invalidateTags([
          {
            type: "note-for-appointment",
            id: appointmentId,
          },
        ]),
      );
    },
    [patientId, appointmentId, dispatch],
  );

  const saveComment = useCallback(
    async (commentToSave: string) => {
      if (!noteDataId || !decodeKey) {
        setIsSavingComment(false);
        return;
      }

      setIsDirty(false);
      setIsSavingComment(true);

      try {
        await putNoteComment({
          note_data_id: noteDataId,
          note_comment: await orchid_encrypt(commentToSave, decodeKey),
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsSavingComment(false);
      }
    },
    [decodeKey, noteDataId],
  );

  const saveCommentDebounced = useMemo(
    () => debounce(saveComment, AUTOSAVE_DEBOUNCE),
    [saveComment],
  );

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    saveCommentDebounced(comment);
  }, [comment, isDirty, saveCommentDebounced]);

  useEffect(() => {
    if (isLoadingPreexistingNotes || !preexistingNotes?.length) {
      return;
    }

    const note = preexistingNotes[0];
    setNoteDataId(note.note_data_id);

    initializeWithPreexistingNote();
  }, [
    isLoadingPreexistingNotes,
    preexistingNotes,
    initializeWithPreexistingNote,
  ]);

  const [noteTemplateSearch, setNoteTemplateSearch] = useState("");
  const noteSchemasQuery = useQuery(listSchemasQueryOptions("note"));
  const noteSchemas = useMemo(
    () => noteSchemasQuery.data ?? [],
    [noteSchemasQuery.data],
  );
  const [noteSchema, setNoteSchema] = useState<NoteSchema>();

  const filtered = useMemo(() => {
    if (!noteTemplateSearch) {
      return noteSchemas;
    }

    return noteSchemas.filter((el) =>
      el.title?.toLowerCase().includes(noteTemplateSearch.toLowerCase()),
    );
  }, [noteSchemas, noteTemplateSearch]);

  const currSelectedNoteSchema = noteSchemas.find(
    (el) => el.note_schema_id === noteSchema?.note_schema_id,
  );

  return (
    <div className={styles.container}>
      {isLoading && <Loading />}

      {!isLoading && !noteDataId && (
        <>
          <div>
            Orchid Psychotherapy Note is linked to a session progress note. Add
            a progress note template to fill in Psychotherapy Note.
          </div>
          <div className={styles["note-type"]}>
            <Form.Label className={styles["label"]}>
              Progress Note (optional)
            </Form.Label>
            <DropdownToggle
              menuClassName={styles["menu"]}
              titleClassName={styles["title"]}
              title={
                <div>
                  <OutlineDocumentFilled />
                  <input
                    type="text"
                    placeholder="Choose from below template"
                    onChange={(event) => {
                      setNoteSchema(undefined);
                      setNoteTemplateSearch(event.currentTarget.value);
                    }}
                    value={
                      currSelectedNoteSchema
                        ? currSelectedNoteSchema.title
                        : noteTemplateSearch
                    }
                    autoFocus
                  />
                </div>
              }
              Caret={<OutlineChevronDown />}
            >
              {filtered.map((el) => (
                <Dropdown.Item
                  key={el.note_schema_id}
                  onClick={() => {
                    setNoteSchema(el);
                  }}
                >
                  {el.title}
                </Dropdown.Item>
              ))}
            </DropdownToggle>
          </div>
          <Button
            disabled={!noteSchema}
            onClick={() => {
              noteSchema && attachNewNote(noteSchema);
            }}
          >
            Add Note Template
          </Button>
        </>
      )}

      {!isLoading && noteDataId && (
        <>
          <div className={styles["disclaimer-row"]}>
            <div>
              Psychotherapy Note is kept separate from the client record.
            </div>
            <CopyToClipboardButton
              onClick={() => navigator.clipboard.writeText(comment)}
              size="small"
            />
          </div>
          <textarea
            ref={commentInputRef}
            className={styles["note-input"]}
            onChange={(event) => {
              setComment(event.target.value);
              setIsDirty(true);
            }}
            value={comment}
            autoFocus
          />
          <Button
            variant="primary"
            size="sm"
            disabled={isSavingComment || !isDirty}
            onClick={() => {
              saveCommentDebounced.cancel();
              saveComment(comment);
            }}
          >
            {isSavingComment ? "Saving..." : isDirty ? "Save" : "Saved"}
          </Button>
        </>
      )}
    </div>
  );
};

export default PsychotherapyNotes;
